import { CommonModule, DATE_PIPE_DEFAULT_OPTIONS } from '@angular/common';
import { provideHttpClient, withInterceptors, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import { ErrorHandler, NgModule, inject, provideAppInitializer } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@env/environment';
import { MissingTranslationHandler, provideTranslateCompiler, provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import * as Sentry from '@sentry/angular';
import { ChatApiInterceptor } from '@shared/modules/chat/chat-api.interceptor';
import { TranslateMessageFormatCompiler } from 'ngx-translate-messageformat-compiler';
import { ApiInterceptor } from './interceptors/api.interceptor';
import { DateInterceptor } from './interceptors/date.interceptor';
import { TranslateHandler } from './misc/locale.utils';
import { MaterialConfigModule } from './modules/material-config.module';
import { ResponseLoggerModule } from './modules/response-logger/response-logger.module';
import { SessionCheckModule } from './modules/session-check/session-check.module';
import { StaleBundleModule } from './modules/stale-bundle/stale-bundle.module';
import { StartupService } from './services/startup.service';
import { SvgIconsService } from './services/svg-icons.service';
import { MatIconModule } from '@angular/material/icon';

const sharedModules = [MaterialConfigModule];

@NgModule({
  declarations: [],
  exports: [...sharedModules],
  imports: [CommonModule, SessionCheckModule, StaleBundleModule, ResponseLoggerModule, MatIconModule, ...sharedModules],
  providers: [
    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: './assets/locales/',
        suffix: '.json?v=' + environment.buildVersion
      }),
      compiler: provideTranslateCompiler(TranslateMessageFormatCompiler),
      // Explicit provider instead of provideMissingTranslationHandler(): the helper
      // detects classes via `/^class\s/.test(fn.toString())`, which fails for
      // undecorated classes in minified builds (esbuild emits them as anonymous
      // `X=class{...}` expressions) and then invokes the class without `new`.
      missingTranslationHandler: { provide: MissingTranslationHandler, useClass: TranslateHandler }
    }),
    provideAppInitializer(() => {
      const initializerFn = (
        (startupService: StartupService) => () =>
          startupService.run()
      )(inject(StartupService));
      return initializerFn();
    }),
    provideHttpClient(withXhr(), withInterceptors([ApiInterceptor, ChatApiInterceptor, DateInterceptor])),

    { provide: DATE_PIPE_DEFAULT_OPTIONS, useValue: { dateFormat: environment.formats.date } },
    StartupService,
    SvgIconsService,
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
        logErrors: false
      })
    },
    {
      provide: Sentry.TraceService,
      deps: [Router]
    }
  ]
})
export class CoreModule {}
